import Header from './components/Header';
import Body from './components/Body';
import { styles } from './styles';
import classNames from 'classnames';
import Section, { SectionType } from './components/Section';
import Summary from './components/Summary';
import Link from 'next/link';

const items: SectionType[] = [
  {
    title: '1. Acceptance of Terms',
    content: (
      <>
        By accessing or using the MindFeed application, website, or any other
        associated services (collectively referred to as the &#34;Service&#34;),
        you acknowledge and agree to comply with these Terms of Use, our Privacy
        Policy, and any additional policies or guidelines that we may provide.
        These documents collectively govern your use of the Service.
        <br />
        <br />
        If you do not agree with any part of these Terms, you are not authorized
        to access or use the Service and must discontinue any use of it
        immediately. Your use of the Service signifies your acceptance of these
        Terms and any updates to them.
        <br />
        <br />
        MindFeed reserves the right to modify, update, or revise these Terms at
        any time, and such changes will be effective immediately upon being
        posted to our website. It is your responsibility to review these Terms
        periodically. If you continue to use the Service after we have posted
        any changes to these Terms, you will be deemed to have accepted those
        changes.
        <br />
        <br />
        These Terms apply to all users of the Service, including those who
        access the Service via mobile devices, apps, or third-party platforms.
        You agree that any updates or new features added to the Service will be
        subject to these Terms.
        <br />
        <br />
        If you are accessing or using the Service on behalf of an organization
        or entity, you represent and warrant that you have the authority to bind
        that organization to these Terms. In such cases, the terms &#34;you&#34;
        and &#34;your&#34; will refer to both you individually and the
        organization or entity you represent.
      </>
    ),
    summary: (
      <>
        By using MindFeed, you agree to comply with these Terms of Use, our
        Privacy Policy, and any updates to them.
      </>
    ),
  },
  {
    title: '2. Changes to the Terms',
    content: (
      <>
        MindFeed reserves the right to modify, update, or temporarily or
        permanently discontinue any aspect of the Service, including features,
        functionalities, and content, at any time, without prior notice. These
        changes may include the introduction of new features, tools, or
        services, as well as changes to the pricing or availability of the
        Service.
        <br />
        <br />
        While we strive to provide a consistent and reliable experience, we
        cannot guarantee that the Service will be available at all times or that
        it will meet your specific requirements. In some cases, certain features
        or functionalities of the Service may be removed or altered due to
        technical, legal, or business considerations.
        <br />
        <br />
        You acknowledge and agree that MindFeed is not liable for any loss,
        damage, or inconvenience caused by modifications, suspension, or
        discontinuation of any aspect of the Service. You are responsible for
        keeping up-to-date with any changes to the Service, and by continuing to
        use the Service after any modifications, you accept and agree to be
        bound by those changes.
        <br />
        <br />
        If any significant changes are made to the Service that materially
        affect your use, we will make reasonable efforts to notify you, whether
        via email or through notifications within the Service.
      </>
    ),
    summary: (
      <>
        MindFeed may modify, suspend, or discontinue any part of the Service at
        any time, and users are responsible for staying informed about these
        changes.
      </>
    ),
  },
  {
    title: '3. Registration and Account Use',
    content: (
      <>
        To access and use the full features of MindFeed, you must create an
        account by signing up through one of the available third-party services:
        Google, Facebook, or Apple. By using these services to register, you
        agree to comply with the terms and conditions of the respective
        third-party provider, in addition to these Terms of Use.
        <br />
        <br />
        You are solely responsible for ensuring that your account information is
        accurate and up-to-date. It is essential that you maintain the
        confidentiality of your login credentials, including your account
        details provided through Google, Facebook, or Apple. You agree not to
        share your login information with anyone else and to take all necessary
        steps to prevent unauthorized access to your account.
        <br />
        <br />
        You are also responsible for all activities that occur under your
        account, whether or not you authorized them. This includes ensuring that
        all activity complies with the terms of these Terms of Use. If you
        suspect any unauthorized use of your account or if your login
        credentials have been compromised in any way, you must immediately
        notify us through the contact information provided within the Service.
        We may take actions to protect your account, but ultimately, you are
        responsible for safeguarding your account&#39;s security.
        <br />
        <br />
        In addition, you agree to promptly update any information that may be
        necessary for maintaining the security and functionality of your
        account. If we suspect any fraudulent or malicious activity on your
        account, we reserve the right to suspend or terminate your access to the
        Service, as outlined in these Terms of Use.
        <br />
        <br />
        By using MindFeed, you confirm that the information you provide during
        registration is truthful, and you acknowledge that your access to the
        Service may be subject to verification or validation procedures as
        deemed necessary by us.
      </>
    ),
    summary: (
      <>
        You must sign up with Google, Facebook, or Apple and are responsible for
        maintaining your account&#39;s security.
      </>
    ),
  },
  {
    title: '4. Authorized Use',
    content: (
      <>
        By using MindFeed, you agree to comply with all applicable laws and
        these Terms of Use. You must use the Service responsibly and ethically.
        Specifically, you agree not to:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li>
            <p className="font-bold mb-5">Violate the rights of others</p>
            <ul className="list-disc pl-5 mb-5">
              <li>
                Infringe on the privacy, intellectual property, or other legal
                rights of MindFeed users or third parties.
              </li>
              <li>Harass, impersonate, or mislead other users.</li>
            </ul>
          </li>
          <li>
            <p className="font-bold mb-5">Share or upload prohibited content</p>
            <ul className="list-disc pl-5 mb-5">
              <li>
                Post, upload, or share any content that is illegal, harmful,
                threatening, defamatory, abusive, obscene, hateful, or otherwise
                inappropriate.{' '}
              </li>
              <li>
                Distribute content that promotes violence, discrimination, or
                any unlawful activity.{' '}
              </li>
            </ul>
          </li>
          <li>
            <p className="font-bold mb-5">
              Tamper with MindFeed’s security or functionality
            </p>
            <ul className="list-disc pl-5">
              <li>
                Attempt to hack, disrupt, or bypass any security measures
                protecting the Service.
              </li>
              <li>
                Use automated tools (bots, scrapers, etc.) to access, collect,
                or modify data from MindFeed without authorization.
              </li>
              <li>
                Interfere with or alter the normal operation of the app in any
                unauthorized manner.
              </li>
            </ul>
          </li>
        </ul>
        <br />
        Any violation of these terms may result in the suspension or termination
        of your access to MindFeed.
      </>
    ),
    summary: (
      <>
        You agree to use MindFeed legally, respect others&#39; rights, and not
        disrupt the Service.
      </>
    ),
  },
  {
    title: '5. Intellectual Property',
    content: (
      <>
        All content, features, and design elements of MindFeed, including but
        not limited to:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li>Software and source code,</li>
          <li>User interfaces and user experience,</li>
          <li>Graphics, icons, and illustrations,</li>
          <li>Logos, trade names, and trademarks,</li>
          <li>Text, descriptions, and other written content,</li>
          <li>Audiovisual elements, animations, and videos,</li>
        </ul>
        <br />
        are protected by applicable intellectual property laws and remain the
        exclusive property of MindFeed or its licensors.
        <br />
        <br />
        <strong>Usage Restrictions</strong>
        <br />
        <br />
        Unless expressly authorized by MindFeed, you are strictly prohibited
        from:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li>
            Copying, reproducing, modifying, or adapting any part of the
            Service,
          </li>
          <li>
            Distributing, selling, renting, or transferring any content from the
            application,
          </li>
          <li>
            Decompiling, disassembling, or attempting to extract the source
            code,
          </li>
          <li>
            Using any MindFeed trademarks or logos without prior written
            consent.
          </li>
        </ul>
      </>
    ),
    summary: (
      <>
        MindFeed&#39;s content and design are protected by intellectual property
        laws and cannot be used without authorization.
      </>
    ),
  },
  {
    title: '6. User Content',
    content: (
      <>
        By using MindFeed, you retain full ownership of the notes, collections,
        and any other content you create or submit through the Service. However,
        to ensure the proper functioning of MindFeed, you grant us certain
        rights to use your content, as outlined below:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li className="mb-5">
            <strong>Ownership:</strong> You remain the sole owner of all
            intellectual property rights related to the content you submit.
            MindFeed does not claim ownership over your notes or collections.
          </li>
          <li className="mb-5">
            <strong>License Grant:</strong> By submitting content, you provide
            MindFeed with a global, non-exclusive, royalty-free license to:
            <ul className="list-disc pl-5">
              <li>Store, process, and display your content within the app.</li>
              <li>
                Share your content as needed when you use MindFeed’s sharing
                features.
              </li>
              <li>
                Optimize the functionality of the Service (e.g., enabling
                search, backups, and cross-device access).
              </li>
            </ul>
          </li>
          <li className="mb-5">
            <strong>Privacy and Security:</strong> MindFeed does not publicly
            share your content unless you choose to do so via the sharing
            features provided within the app.
          </li>
          <li className="mb-5">
            <strong>Content Responsibility:</strong> You are solely responsible
            for the content you submit, including ensuring it does not violate
            applicable laws, third-party rights, or these Terms of Use.
          </li>
        </ul>
        <br />
        This license is limited to what is necessary for the operation of
        MindFeed and does not grant us permission to use your content for
        unrelated purposes.
      </>
    ),
    summary: (
      <>
        You retain ownership of your content but grant MindFeed a license to
        store, process, and share it as needed for the Service.
      </>
    ),
  },
  {
    title: '7. Privacy',
    content: (
      <>
        Your privacy is a priority for us. Please refer to our{' '}
        <Link className="text-mf-blue-base" href="/privacy-policy">
          Privacy Policy
        </Link>{' '}
        to understand how we collect, use, and protect your personal
        information.
      </>
    ),
  },
  {
    title: '8. Use Restrictions',
    content: (
      <>
        When using MindFeed, you agree to comply with all applicable laws and
        refrain from any misuse of the Service.
        <br />
        <br />
        Specifically, you agree not to:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li className="mb-5">
            <p className="font-bold mb-5">
              Send Spam or Unsolicited Communications
            </p>
            <ul className="list-disc pl-5">
              <li>
                You may not use MindFeed to send bulk, repetitive, or unwanted
                messages to other users.
              </li>
              <li>
                You may not use automated systems (bots, scripts, or similar
                technologies) to send messages or interact with the Service in a
                disruptive manner.
              </li>
            </ul>
          </li>
          <li className="mb-5">
            <p className="font-bold mb-5">Disrupt the Use of the Service</p>
            <ul className="list-disc pl-5">
              <li>
                You must not interfere with or impair the experience of other
                users, such as by attempting to overload the system, introducing
                malware, or using any means to disable the Service.
              </li>
              <li>
                You are prohibited from attempting to access another user’s
                account, data, or private content without their explicit
                permission.
              </li>
            </ul>
          </li>
          <li className="mb-5">
            <p className="font-bold mb-5">Impersonation and Account Misuse</p>
            <ul className="list-disc pl-5">
              <li>
                You may not create multiple accounts to manipulate the Service,
                evade restrictions, or gain unauthorized access to features.
              </li>
              <li>
                You must not impersonate another individual or entity, including
                through misleading usernames or profile information.
              </li>
            </ul>
          </li>
        </ul>
        <br />
        MindFeed reserves the right to suspend or terminate accounts that
        violate these restrictions.
      </>
    ),
    summary: (
      <>
        Users must not send spam, disrupt the service, access other accounts, or
        create fake identities.
      </>
    ),
  },
  {
    title: '9. Limitation of Liability',
    content: (
      <>
        MindFeed provides its services &#34;as is&#34; and does not guarantee
        that they will always function without errors, interruptions, or
        security vulnerabilities. By using the Service, you acknowledge and
        accept the following limitations:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li className="mb-5">
            <strong>No Warranty of Error-Free Operation</strong> - While we
            strive to maintain a reliable and secure platform, we do not
            guarantee that MindFeed will always be free of errors, bugs,
            viruses, or other harmful components.
          </li>
          <li className="mb-5">
            <strong>Use at Your Own Risk</strong> - You assume full
            responsibility for your use of MindFeed, including any reliance on
            the accuracy or completeness of the content you store or access
            through the Service.
          </li>
          <li className="mb-5">
            <strong>No Liability for Damages</strong> - To the fullest extent
            permitted by law, MindFeed and its affiliates, employees, and
            partners shall not be held liable for any direct, indirect,
            incidental, special, or consequential damages arising from:
            <ul className="list-disc pl-5">
              <li>The use or inability to use the Service.</li>
              <li>Unauthorized access to or alteration of your content.</li>
              <li>Any security breaches, data loss, or service disruptions.</li>
            </ul>
          </li>
          <li className="mb-5">
            <strong>External Factors</strong> - We are not responsible for
            issues caused by third-party services, network failures, or external
            events beyond our control.
          </li>
        </ul>
        <br />
        By using MindFeed, you acknowledge these limitations and agree that
        MindFeed cannot be held responsible for any loss or damage related to
        your use of the Service.
      </>
    ),
    summary: (
      <>
        MindFeed is not liable for any errors, damages, or losses arising from
        the use of the Service, and users assume all risks associated with it.
      </>
    ),
  },
  {
    title: '10. Billing',
    content: (
      <>
        MindFeed offers both free and premium plans. Subscription pricing may
        vary depending on your location and the billing information provided at
        the time of purchase.
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li className="mb-5">
            <p className="font-bold">a. Subscriptions and Renewals</p>
            <p>
              If you subscribe to MindFeed Premium, you can choose between a
              monthly or annual plan. Your subscription will automatically renew
              at the end of each billing cycle. We will notify you before your
              subscription renews. You can cancel at any time, but fees already
              paid are non-refundable.
            </p>
          </li>
          <li className="mb-5">
            <p className="font-bold">b. Cancellation</p>
            <p>
              You may cancel your subscription at any time through your account
              settings. Once canceled, you will retain access to premium
              features until the end of your current billing cycle. However, no
              refunds or credits will be issued for fees already charged, and
              any outstanding payments will remain due.
            </p>
          </li>
          <li className="mb-5">
            <p className="font-bold">c. Free Trials</p>
            <p>
              MindFeed may offer free trials to allow you to explore premium
              features. Eligibility and trial duration are at MindFeed’s
              discretion. If you do not cancel before the end of your free
              trial, your selected payment method will be charged for the next
              billing cycle.
            </p>
          </li>
          <li className="mb-5">
            <p className="font-bold">d. Pricing Changes</p>
            <p>
              MindFeed reserves the right to modify its pricing. Any changes
              will take effect at the next renewal date or 30 days after notice,
              whichever is later.
            </p>
          </li>
          <li className="mb-5">
            <p className="font-bold">e. Billing Communications</p>
            <p>
              You agree that MindFeed may contact you via email, push
              notifications, or other methods regarding your subscription,
              billing, and account usage.
            </p>
          </li>
        </ul>
      </>
    ),
    summary: (
      <>
        MindFeed offers free and premium plans with automatic renewals, no
        refunds after cancellation, and possible pricing changes with prior
        notice.
      </>
    ),
  },
  {
    title: '11. Suspension and Termination',
    content: (
      <>
        To ensure the safety and integrity of MindFeed, we reserve the right to
        suspend or terminate access to the Service if a user violates these
        Terms of Use in a substantial manner. This action may be taken if a user
        engages in any activity that negatively impacts the functioning of the
        Service, other users, or violates the rights of MindFeed or third
        parties.
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li className="mb-5">
            <strong>Violation of Terms:</strong> A substantial violation
            includes, but is not limited to, misuse of the Service, uploading
            illegal or harmful content, or fraudulent activities.
          </li>
          <li className="mb-5">
            <strong>Consequences of Termination:</strong> If your access is
            terminated, you will lose access to your account, all data,
            including notes, collections, and content you have uploaded, unless
            stated otherwise in the Privacy Policy
          </li>
          <li className="mb-5">
            <strong>Notification:</strong> You will be informed of any
            suspension or termination of your account, and you may have the
            opportunity to address or resolve the violation if applicable.
          </li>
        </ul>
        <br />
        In such cases, we may also take steps to prevent further misuse of the
        Service, and no refunds will be issued for any remaining subscription or
        payment balances.
      </>
    ),
    summary: (
      <>
        MindFeed can suspend or terminate access if you violate the Terms,
        leading to loss of account and content access.
      </>
    ),
  },
  {
    title: '12. Third-Party Services',
    content: (
      <>
        You may choose to use MindFeed in conjunction with third-party websites,
        platforms, or applications. Your use of any third-party service is
        subject to its own terms and conditions. MindFeed makes no
        representations or warranties regarding third-party services and
        disclaims all liability for any issues arising from their use.
      </>
    ),
    summary: (
      <>
        MindFeed is not responsible for third-party services you choose to use
        with the websites, platforms, or applications.
      </>
    ),
  },
  {
    title: '13. Your Indemnity Obligations',
    content: (
      <>
        You agree, to the extent permitted by law, to defend, indemnify, and
        hold harmless MindFeed, its affiliates, officers, directors, agents,
        licensors, and employees from any claims, costs, damages, losses,
        liabilities, and expenses (including reasonable legal fees) arising
        from: <br />
        <br />
        <ul className="list-disc pl-5">
          <li className="mb-5">
            <strong>Your violation of these Terms, or</strong>
          </li>
          <li className="mb-5">
            <strong>Your content submitted through the Service.</strong>
          </li>
        </ul>
        <br />
        If MindFeed incurs any harm or legal consequences due to your content or
        a violation of these Terms, or if a third party holds MindFeed
        responsible for your actions, you will be responsible for covering any
        related costs.{' '}
      </>
    ),
    summary: (
      <>
        You are responsible for any costs MindFeed incurs due to your content or
        violations of these Terms.
      </>
    ),
  },
  {
    title: '14. Term and Termination',
    content: (
      <>
        <strong>a. Term</strong>
        <br />
        <br />
        These Terms take effect upon your first access to the Service and remain
        in force until:
        <br />
        <br />
        {'i) '}if you are a paying subscriber, the expiration or termination of
        your subscription; or
        <br />
        {'ii) '}if you are using MindFeed&#39;s free offering, when your account
        is deleted or terminated.
        <br />
        <br />
        <strong>b. Violations</strong>
        <br />
        <br />
        If MindFeed reasonably determines that you have violated these Terms
        through your use of the Service, your User Content, or your actions,
        MindFeed may take one or more of the following actions:
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li className="mb-5">Delete the prohibited User Content;</li>
          <li className="mb-5">Suspend your access to the Service;</li>
          <li className="mb-5">
            Terminate and delete your account, along with all associated User
            Content;
          </li>
          <li className="mb-5">Permanently ban you from using the Service;</li>
          <li className="mb-5">
            Report your User Content to the appropriate authorities if
            necessary.
          </li>
        </ul>
        <br />
        If you break the rules, we have the right to remove you and everything
        in your account from the Service.
        <br />
        <br />
        <strong>c. Effect of Termination</strong>
        <br />
        <br />
        If your subscription is terminated due to a fault on MindFeed&#39;s
        part, you will receive a prorated refund for the prepaid fees covering
        the remaining period after termination.
        <br />
        <br />
        If your subscription is terminated due to a violation on your part, no
        refund will be provided, and you must immediately pay any outstanding
        fees for the remaining subscription period.
        <br />
        <br />
        Upon expiration or termination of your subscription, you must stop using
        the Service. You will lose access to your User Content and any stored
        data, and we may delete such data unless legally prohibited. However,
        User Content included in shared items with other users may remain
        accessible within those items even after your subscription expires.
        <br />
        <br />
        <strong>d. Survival of Terms</strong>
        <br />
        <br />
        The sections titled &#34;Term and Termination,&#34; &#34;Billing,&#34;
        &#34;Intellectual Property,&#34; &#34;Limitation of Liability,&#34; and
        &#34;Miscellaneous&#34; will continue to apply even after these Terms
        expire or are terminated.
      </>
    ),
    summary: (
      <>
        You are responsible for any costs MindFeed incurs due to your content or
        violations of these Terms.
      </>
    ),
  },
  {
    title: '15. Governing Law and Dispute Resolution',
    content: (
      <>
        This section outlines the legal framework that governs your use of
        MindFeed and the process for resolving any disputes that may arise.
        <br />
        <br />
        <ul className="list-disc pl-5">
          <li className="mb-5">
            <strong>Governing Law:</strong> These Terms are subject to the laws
            of Australia, meaning that any issues, claims, or disputes related
            to your use of the Service will be resolved according to Australian
            law. This ensures a consistent and predictable legal framework for
            all users, regardless of their location.{' '}
          </li>
          <li className="mb-5">
            <strong>Dispute Resolution:</strong> If any disputes or conflicts
            arise in connection with the Service, you agree to resolve them
            through legal means within the jurisdiction of Australia. This means
            that any legal action or claim must be filed and resolved in the
            appropriate courts located in Australia.{' '}
          </li>
          <li className="mb-5">
            <strong>Exclusive Jurisdiction:</strong> By agreeing to these Terms,
            you acknowledge that only the courts in Australia have the authority
            to hear disputes related to MindFeed. You waive the right to seek
            resolution in any other jurisdiction, providing clarity for both
            parties in case legal action becomes necessary.{' '}
          </li>
        </ul>
      </>
    ),
    summary: (
      <>
        If there is a dispute about your use of MindFeed, Australian law will
        apply, and the matter will be handled by Australian courts.
      </>
    ),
  },
  {
    title: '16. Miscellaneous',
    content: (
      <>
        <strong>a. Compliance with Applicable Law</strong>
        <br />
        <br />
        You agree to comply with all applicable local, national, and
        international laws, treaties, and regulations in connection with your
        use of the Service. MindFeed also agrees to comply with all applicable
        laws, treaties, and regulations when providing the Service.
        <br />
        <br />
        <strong>b. Governing Law and Jurisdiction</strong>
        <br />
        <br />
        These Terms will be governed by and construed in accordance with the
        laws of Australia, without regard to its conflict of laws principles.
        Any disputes will be resolved in Australian courts.
        <br />
        <br />
        <strong>c. Export Restrictions</strong>
        <br />
        <br />
        The Service is subject to trade sanctions and laws regulating the
        import, export, and use of the Service. You agree to comply with these
        laws and regulations, and you warrant that you are not prohibited from
        accessing the Service. You also agree not to make the Service available
        to anyone who is prohibited from accessing it under the laws or
        regulations of any jurisdiction.
        <br />
        <br />
        <strong>d. Dispute Resolution</strong>
        <br />
        <br />
        In case of a dispute, please contact us to attempt resolution. If no
        agreement is reached, disputes arising from these Terms will be resolved
        through binding arbitration. The arbitration will be conducted via
        videoconferencing or at a mutually agreed location. The arbitration will
        be carried out according to applicable rules, and the decision may be
        entered into a competent court.
        <br />
        <br />
        <strong>e. Headings and Explanations</strong>
        <br />
        <br />
        Headings used in these Terms and the explanatory boxes are provided for
        convenience only and will not in any way affect the meaning or
        interpretation of the Terms or any portion thereof.
        <br />
        <br />
        <strong>f. Severability</strong>
        <br />
        <br />
        If a provision of these Terms is found to be invalid or unenforceable,
        the other provisions will remain in full force and effect.
        <br />
        <br />
        <strong>g. Waiver</strong>
        <br />
        <br />
        MindFeed&#39;s failure to enforce any provision of these Terms does not
        constitute a waiver of the right to enforce that provision in the
        future.
        <br />
        <br />
        <strong>h. Notices</strong>
        <br />
        <br />
        All required notices will be sent to the email address associated with
        your account or by any other legally permissible means.
        <br />
        <br />
        <strong>i. Intellectual Property</strong>
        <br />
        <br />
        We respect intellectual property rights. If you believe your work has
        been infringed, please refer to our intellectual property policy.
        <br />
        <br />
        <strong>j. Changes to These Terms</strong>
        <br />
        <br />
        MindFeed reserves the right to modify these Terms at any time. Advance
        notice will be provided for significant changes, and your continued use
        of the Service constitutes acceptance of the modified Terms.
        <br />
        <br />
        <strong>k. Changes to the Service</strong>
        <br />
        <br />
        MindFeed may update or discontinue features at any time. If a service is
        discontinued during your subscription, a similar alternative may be
        provided, or a refund will be issued.
        <br />
        <br />
        <strong>l. Entire Agreement</strong>
        <br />
        <br />
        These Terms, along with any referenced policies, constitute the entire
        agreement between you and MindFeed regarding the Service.
      </>
    ),
    summary: (
      <>
        This section outlines compliance with laws, dispute resolution, and
        terms for modifying or discontinuing the service, as well as general
        provisions like severability and waiver.
      </>
    ),
  },
  {
    title: '17. Contact',
    content: (
      <>
        If you have any questions regarding these Terms of Use, please contact
        us at:{' '}
        <Link className="text-mf-blue-base" href="mailto:support@mindfeed.app">
          support@mindfeed.app
        </Link>
        .
      </>
    ),
  },
];

const TermsOfUse = (): JSX.Element => (
  <div className="p-0 pt-16 flex flex-col font-inter text-mf-off-black-950">
    <Header title="Terms of Use" />
    <Body>
      <div className="mb-12">
        <div className={classNames(styles.text, 'mb-8')}>
          These Terms of Use are effective as of February 13, 2025.
          <br />
          <br />
          Thank you for using MindFeed (the &#34;Service&#34;). By accessing and
          using our application and website, you agree to comply with these
          Terms of Use (“Terms”), which apply to you (“you” or “your”).
          <br />
          <br />
          By using the Service, you agree that these Terms will become a legally
          binding agreement between you and the MindFeed Contracting Entity
          identified in these Terms (“MindFeed”). If you do not agree to these
          Terms, please do not use our Service.
        </div>
        <Summary>
          At MindFeed, we believe in simplicity! These boxes explain our Terms
          in a simple way, but it is important to note that only the Terms
          outside of these boxes are legally valid.
        </Summary>
      </div>
      {items.map((item, i) => (
        <Section {...item} key={i} />
      ))}
    </Body>
  </div>
);

export default TermsOfUse;
